"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.SensorReading = void 0;
/* eslint-disable no-bitwise */
const binary_parser_1 = require("binary-parser");
const SensorImage_1 = require("./SensorImage");
const ndarray_1 = __importDefault(require("ndarray"));
const sensor_32053_bss_001_lens_table_1 = require("./lens-tables/sensor-32053-bss-001-lens-table");
const sensor_32053_bss_101_lens_table_1 = require("./lens-tables/sensor-32053-bss-101-lens-table");
const _headerParser = new binary_parser_1.Parser()
    .endianess('little')
    .uint8('headerVersion')
    .uint16('frameCounter')
    .uint16('timestamp')
    .uint16('tofcosSubVersion')
    .uint16('tofcosVersion')
    .uint8('hardwareVersion')
    .uint16('chipId')
    .uint16('imageWidth')
    .uint16('imageHeight')
    .uint16('imageOriginX')
    .uint16('imageOriginY')
    .uint16('currentIntegrationTime3dWFOV')
    .uint16('currentIntegrationTime3dNFOV')
    .uint16('currentIntegrationTimeGrayscale')
    .uint16('integrationTimeGrayscale')
    .uint16('integrationTime0WFOV')
    .uint16('integrationTime1WFOV')
    .uint16('integrationTime2WFOV')
    .uint16('integrationTime3WFOV')
    .uint16('integrationTime4NFOV')
    .uint16('integrationTime5NFOV')
    .uint16('interferenceLevelDetection')
    .uint16('edgeDetectionThreshold')
    .uint16('amplitudeLimit0WFOV')
    .uint16('amplitudeLimit1WFOV')
    .uint16('amplitudeLimit2WFOV')
    .uint16('amplitudeLimit3WFOV')
    .uint16('amplitudeLimit4NFOV')
    .uint16('unused1')
    .uint8('binningType')
    .uint16('distanceTemporalFilterFactorWFOV')
    .uint16('distanceTemporalFilterThresholdWFOV')
    .uint16('singleValueTemporalFilterFactorNFOV')
    .uint16('singleValueTemporalFilterThresholdNFOV')
    .uint8('modulationFrequency')
    .uint8('modulationChannel')
    .bit1('temporalHDRFlag')
    .bit1('inputPinFlag')
    .bit1('useLastValueFlag')
    .bit1('reducedIlluminationPowerFlag')
    .bit1('notUsedFlag1')
    .bit1('notUsedFlag2')
    .bit1('notUsedFlag3')
    .bit1('notUsedFlag4')
    .bit1('autoModulationChannelFlag')
    .bit1('autoIntegrationTimeFlag')
    .bit1('averageFilterFlag')
    .bit1('medianFilterFlag')
    .bit1('compensatedDRNUFlag')
    .bit1('compensatedTemperatureFlag')
    .bit1('compensatedAmbientLightFlag')
    .bit1('spatialHDR')
    .int16('temperature')
    .uint8('fov')
    .uint16('nfovSpotDataDistance')
    .uint16('nfovSpotDataAmplitude')
    .uint8('nfovSpotXCoordinate')
    .uint8('nfovSpotYCoordinate')
    .uint16('reserved1');
class SensorReading extends SensorImage_1.SensorImage {
    header;
    pixelMatrix;
    static BINNED_WIDTH = 80;
    static BINNED_HEIGHT = 30;
    static MAX_VALID_DISTANCE = 15999;
    constructor({ data }) {
        const pixelMatrix = SensorReading.parsePixelData(data);
        super(SensorReading.BINNED_WIDTH, SensorReading.BINNED_HEIGHT, SensorReading.computeStats(pixelMatrix));
        this.pixelMatrix = pixelMatrix;
        this.header = _headerParser.parse(new Uint8Array(data.slice(0, 80)));
    }
    getRaw(x, y) {
        if (!Number.isInteger(x) || !Number.isInteger(y) || x < 0 || y < 0 || x >= this.width || y >= this.height) {
            throw new Error(`invalid pixel index (${x}, ${y})`);
        }
        return this.pixelMatrix[y][x];
    }
    detectPartNumber() {
        // Detect the old prototypes with the narrower lens by checking OS version and hardware version
        if (this.header['hardwareVersion'] === 3 && this.header['tofcosVersion'] === 3 && this.header['tofcosSubVersion'] === 3) {
            return '32053-BSS-001';
        }
        return '32053-BSS-101';
    }
    fieldOfView() {
        if (this.detectPartNumber() === '32053-BSS-001') {
            return {
                xDegrees: 50 /* degrees */,
                yDegrees: 19 /* degrees */,
            };
        }
        return {
            xDegrees: 100 /* degrees */,
            yDegrees: 40 /* degrees */,
        };
    }
    pixelWidthOnSensorInMm() {
        let pixelSize = 0.02;
        if (this.width === SensorReading.BINNED_WIDTH) {
            pixelSize *= 2;
        }
        return pixelSize;
    }
    asArray() {
        const array = new Array(this.height * this.width * 2);
        for (let y = 0; y < this.height; y++) {
            for (let x = 0; x < this.width; x++) {
                const { depth, amplitude } = this.getRaw(x, y);
                const index = y * this.width + x;
                array[index] = depth;
                array[index + this.height * this.width] = amplitude;
            }
        }
        return (0, ndarray_1.default)(array, [2, this.height, this.width]);
    }
    pixelDepthToCameraCoordinates(pixelX, pixelY, depth) {
        const lensTable = this.lensTable();
        return this.toCameraCoordinatesWithLensTable({ depth, x: pixelX, y: pixelY, lensTable });
    }
    lensTable() {
        const partNumber = this.detectPartNumber();
        if (partNumber === '32053-BSS-001') {
            return sensor_32053_bss_001_lens_table_1.sensor32053Bss001LensTable;
        }
        return sensor_32053_bss_101_lens_table_1.sensor32053Bss101LensTable;
    }
    static parsePixelData(data) {
        if (data.byteLength !== 9680) {
            throw new Error('Invalid data');
        }
        const view = new Uint8Array(data);
        const matrix = [];
        const headerSize = 80;
        for (let y = 0; y < SensorReading.BINNED_HEIGHT; y++) {
            matrix[y] = [];
            for (let x = 0; x < SensorReading.BINNED_WIDTH; x++) {
                const pixelNumber = y * SensorReading.BINNED_WIDTH + x;
                const indexOfFirstByte = headerSize + pixelNumber * 4;
                const pixelBytes = view.slice(indexOfFirstByte, indexOfFirstByte + 4);
                matrix[y][x] = SensorReading.parseDistanceAmplitudePixel(pixelBytes);
            }
        }
        return matrix;
    }
    static parseDistanceAmplitudePixel(bytes) {
        if (bytes.byteLength !== 4) {
            throw new Error('invalid byte length');
        }
        const reliability = (bytes[1] & 0b1100000) >> 5;
        const depth = ((bytes[1] & 0b00111111) << 8) | bytes[0];
        const amplitude = (bytes[3] << 8) | bytes[2];
        const depthValid = depth > 0 && depth <= SensorReading.MAX_VALID_DISTANCE;
        return {
            depth,
            reliability,
            amplitude,
            depthValid,
        };
    }
    static computeStats(pixelMatrix) {
        const stats = {
            usablePixels: 0,
            errorPixels: 0,
            emptyPixels: 0,
        };
        for (let y = 0; y < SensorReading.BINNED_HEIGHT; y++) {
            for (let x = 0; x < SensorReading.BINNED_WIDTH; x++) {
                const { depth } = pixelMatrix[y][x];
                if (depth === 0) {
                    stats.emptyPixels++;
                }
                if (depth > 0 && depth <= SensorReading.MAX_VALID_DISTANCE) {
                    stats.usablePixels++;
                }
                if (depth > SensorReading.MAX_VALID_DISTANCE) {
                    stats.errorPixels++;
                }
            }
        }
        return stats;
    }
}
exports.SensorReading = SensorReading;
